import { UnauthenticatedError } from '@lib/api';
import { apiUrl } from '@lib/env';
import { sessionResponseSchema, type Operator } from '@lib/operator';

/**
 * The Backoffice has no session of its own to read.
 *
 * Atelier's `vv_session` cookie is `HttpOnly`, so this bundle cannot see it,
 * cannot verify it, and does not hold the HMAC secret that would let it try.
 * Asking the API is not a workaround for that — it is the only correct move:
 * `apps/api` is where the secret lives and where every write is authorised, so
 * its answer is the one that decides anything.
 *
 * @see docs/adr/0004-static-spa-frontend.md
 */

/**
 * The session check keeps its own `fetch` rather than going through
 * `@lib/api`'s `apiRequest`, because it is the call that decides whether the app
 * renders at all: its timeout is deliberately tighter than a screen's, and it
 * must not inherit whatever retry or error shaping the data client grows later.
 *
 * `UnauthenticatedError` is nonetheless the *shared* class. Two same-named error
 * classes in one bundle would each fail the other's `instanceof`, so a 401 from
 * a flags query would sail past `DashboardLayout`'s redirect and render as a
 * generic error — the failure would look like a routing bug and be found nowhere
 * near here.
 */
export { UnauthenticatedError };

/**
 * A hung request must not become an indefinite spinner.
 *
 * This bound has no counterpart in the Server-Component version, because that
 * check ran inside a request the platform would itself time out. Here nothing
 * else is watching: an unreachable API (a dead origin, a black-holed
 * connection) leaves `fetch` pending forever and the gate never resolves, so an
 * employee sits on "Checking your Atelier session…" with nothing to act on.
 * Ten seconds converts that into the retryable error panel.
 */
const SESSION_TIMEOUT_MS = 10_000;

export async function fetchOperator(signal?: AbortSignal): Promise<Operator> {
    const timeout = AbortSignal.timeout(SESSION_TIMEOUT_MS);

    const response = await fetch(apiUrl('/auth/me'), {
        // Same-origin in dev and prod, so this is belt-and-braces — but it is
        // what makes a deliberately cross-origin VITE_API_URL work rather than
        // silently authenticating nobody.
        credentials: 'include',
        headers: { Accept: 'application/json' },
        signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
    });

    // 401 only. A 403 here used to be folded into "go and sign in", which was
    // harmless while no route could refuse an authenticated employee. It cannot
    // stay that way now RBAC exists: `/auth/me` requires only `read-only`, the
    // floor everyone clears, so a 403 means the guard is misconfigured — and
    // bouncing to Atelier would send the employee round a sign-in loop that
    // ends where it started. `SessionUnavailable` says something is wrong and
    // offers a retry, which is the truth.
    if (response.status === 401) throw new UnauthenticatedError();
    if (!response.ok) {
        throw new Error(`GET ${apiUrl('/auth/me')} failed with ${response.status}`);
    }

    return sessionResponseSchema.parse(await response.json()).operator;
}
